import type { ComponentProps } from "react"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconInboxArrowUpFill18 } from "nucleo-ui-fill-18"
import {
	IconArrowLeftOutline18 as ArrowLeft,
	IconBadgeCheckOutline18 as BadgeCheck,
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
	IconChevronUpOutline18 as ChevronUp,
	IconCircleDashedOutline18 as Circle,
	IconSidebarRightOutline18 as PanelRight,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { Button, IconButton, Shortcut, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { DocViewers } from "@/components/doc-viewers"
import { SubscribersButton } from "@/components/notifications/subscribers-button"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { useBackNavigation } from "@/lib/back-navigation"
import { useJoinDoc } from "@/lib/use-doc-presence"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { useThreadSidebarState } from "@/screens/app/sidebar"
import { TOP_BAR_HEIGHT, TOP_BAR_PADDING } from "@/screens/app/top-bar-tokens"
import { pickMoveOnTarget } from "../../hooks/move-on-target"
import { useFocusMode } from "../../hooks/useFocusMode"
import { useThreadsMultiSelectState } from "../../multi-select/threads-multi-select-state"
import { isThreadNoteEditable, useThreadContext } from "../../thread-context"
import { SnoozeButton } from "./snooze-button"
import { ThreadTitle } from "./thread-title"
import { MoreMenu } from "./top-bar-more-menu"
import { UpDownControls } from "./up-down-controls"

interface Props extends ComponentProps<"div"> {
	isEditorEmpty: boolean
	onHighlightButtonClick: () => unknown
	isEditorOpen: boolean
	embedded?: boolean
}

export function TopBar({
	className,
	onHighlightButtonClick,
	isEditorOpen,
	embedded = false,
	...props
}: Props) {
	const {
		thread,
		nextThread,
		prevThread,
		goNext,
		goPrev,
		doneShortcutEnabled = true,
		reviewMode = false,
		threadPath = (id: string) => `/threads/${id}`,
		exitPath = "/threads",
	} = useThreadContext()
	const zero = useZero()
	const navigate = useNavigate()
	const { role } = useRole()
	const { threadIds } = useThreadsMultiSelectState()
	const threadSidebar = useThreadSidebarState()
	const { isFocusMode } = useFocusMode()
	const { goBack } = useBackNavigation()
	useJoinDoc(`thread:${thread.id}`)

	useShortcut(
		"shift+h",
		() => {
			onHighlightButtonClick()
		},
		{ keyup: true, enabled: isThreadNoteEditable(thread) },
	)

	const changeStatusTooltipText = useMemo(() => {
		return thread.state === "PROCESSED" ? "Move to Inbox" : "Mark done"
	}, [thread.state])

	// Pending state-change dispatches, deferred until the route has changed so
	// Zero's optimistic notify never paints the outgoing thread as PROCESSED.
	const pendingDispatchesRef = useRef<Array<() => void>>([])
	const flushPendingDispatches = useCallback(() => {
		if (pendingDispatchesRef.current.length === 0) return
		const actions = pendingDispatchesRef.current
		pendingDispatchesRef.current = []
		actions.forEach((action) => action())
	}, [])
	useEffect(flushPendingDispatches, [thread.id, flushPendingDispatches])
	useEffect(() => flushPendingDispatches, [flushPendingDispatches])

	const moveOnThen = useCallback(
		(dispatch: () => void) => {
			const target = pickMoveOnTarget({
				threadId: thread.id,
				pathname: window.location.pathname,
				nextThread,
				prevThread,
			})
			if (target === "next") {
				pendingDispatchesRef.current.push(dispatch)
				goNext()
			} else if (target === "prev") {
				pendingDispatchesRef.current.push(dispatch)
				goPrev()
			} else {
				void navigate({ href: exitPath, replace: true })
				dispatch()
			}
		},
		[goNext, goPrev, nextThread, prevThread, navigate, thread.id, exitPath],
	)

	const markReviewed = useCallback(() => {
		if (threadIds.length > 0) return
		const threadId = thread.id
		const threadTitle = thread.title
		moveOnThen(() => {
			void zero.mutate(
				mutators.threads.markEscalationReviewed({ id: threadId }),
			).client
			toast.success({
				icon: BadgeCheck,
				title: "Marked reviewed",
				descriptionAction: {
					label: `${threadTitle}`,
					callback: () => void navigate({ href: threadPath(threadId) }),
				},
				onUndo: async () => {
					await mutateOk(
						zero.mutate(
							mutators.threads.markEscalationReviewed({
								id: threadId,
								reviewed: false,
							}),
						),
					)
					void navigate({ href: threadPath(threadId) })
				},
			})
		})
	}, [
		zero,
		moveOnThen,
		navigate,
		thread.id,
		thread.title,
		threadIds.length,
		threadPath,
	])

	const changeStatus = useCallback(() => {
		if (threadIds.length > 0) return
		const threadId = thread.id
		const threadTitle = thread.title
		const isCurrentlyDone = thread.state === "PROCESSED"
		const prevState = thread.state
		const prevSnoozedUntilIso = thread.snoozedUntil
			? new Date(thread.snoozedUntil).toISOString()
			: null
		const prevLastStateChangeAtIso = thread.lastStateChangeAt
			? new Date(thread.lastStateChangeAt).toISOString()
			: null
		const undoChangeStatus = async () => {
			await mutateOk(
				zero.mutate(
					mutators.threads.update({
						id: threadId,
						state: prevState,
						snoozedUntilIso: prevSnoozedUntilIso,
						lastStateChangeAtIso: prevLastStateChangeAtIso,
					}),
				),
			)
			void navigate({ to: "/threads/$id", params: { id: threadId } })
		}
		const dispatchStateChange = () => {
			if (isCurrentlyDone) {
				void zero.mutate(
					mutators.threads.update({
						id: threadId,
						state: "NEW",
						lastStateChangeAtIso: new Date().toISOString(),
					}),
				).client
				toast.success({
					icon: Circle,
					title: "Moved to Inbox",
					descriptionAction: {
						label: `${threadTitle}`,
						callback: () =>
							void navigate({ to: "/threads/$id", params: { id: threadId } }),
					},
					onUndo: undoChangeStatus,
				})
			} else {
				void zero.mutate(
					mutators.threads.update({
						id: threadId,
						state: "PROCESSED",
						lastStateChangeAtIso: new Date().toISOString(),
					}),
				).client
				toast.success({
					title: "Marked done",
					descriptionAction: {
						callback: () =>
							void navigate({ to: "/threads/$id", params: { id: threadId } }),
						label: `${threadTitle}`,
					},
					onUndo: undoChangeStatus,
				})
			}
		}
		moveOnThen(dispatchStateChange)
	}, [
		zero,
		moveOnThen,
		thread.id,
		thread.state,
		thread.title,
		thread.snoozedUntil,
		thread.lastStateChangeAt,
		threadIds.length,
		navigate,
	])

	useShortcut("e", reviewMode ? markReviewed : changeStatus, {
		enabled: role !== "VIEWER" && threadIds.length === 0 && doneShortcutEnabled,
	})

	return (
		<div
			className={cn(
				TOP_BAR_HEIGHT,
				`relative flex items-center border-b border-gray-200 ${TOP_BAR_PADDING} dark:border-gray-700/80`,
				className,
			)}
			{...props}
		>
			<div className="mx-auto flex w-full max-w-[50rem] items-center gap-1">
				{/* Re-render with key so that the useState of the title is re-initialized */}
				{isFocusMode && (
					<IconButton
						Icon={ArrowLeft}
						iconStrokeWidth={2}
						variant="ghost"
						className="mr-2"
						onClick={() => goBack("/threads")}
					/>
				)}
				<ThreadTitle key={thread.id + thread.title} />

				{isFocusMode && <UpDownControls />}

				<div className="z-20 flex flex-row items-center gap-4 lg:gap-1">
					<DocViewers docKey={`thread:${thread.id}`} />
					<MoreMenu
						onHighlightButtonClick={onHighlightButtonClick}
						isEditorOpen={isEditorOpen}
						moveOnThen={moveOnThen}
					/>
					{reviewMode ? (
						<RoleCheck not={["VIEWER"]}>
							<Tooltip
								label={
									<span className="inline-flex gap-2">
										Mark reviewed
										<Shortcut>{ShortcutSymbols.E}</Shortcut>
									</span>
								}
							>
								<Button
									variant="ghost"
									Icon={BadgeCheck}
									onClick={markReviewed}
								>
									Mark reviewed
								</Button>
							</Tooltip>
						</RoleCheck>
					) : (
						<RoleCheck not={["VIEWER"]}>
							<Tooltip
								label={
									<span className="inline-flex gap-2">
										{changeStatusTooltipText}
										<Shortcut>{ShortcutSymbols.E}</Shortcut>
									</span>
								}
							>
								<IconButton
									variant="ghost"
									Icon={
										thread.state === "PROCESSED"
											? IconInboxArrowUpFill18
											: Check
									}
									iconStrokeWidth={1.5}
									onClick={changeStatus}
								/>
							</Tooltip>
						</RoleCheck>
					)}
					<SubscribersButton entityType="thread" entityId={thread.id} />
					{!reviewMode && (
						<RoleCheck not={["VIEWER"]}>
							<div className="flex items-center gap-3">
								<SnoozeButton />
							</div>
						</RoleCheck>
					)}
					{!embedded && (
						<div className="flex items-center gap-2">
							<Tooltip
								label={
									<span className="inline-flex gap-2">
										Navigate down <Shortcut>J</Shortcut>
									</span>
								}
							>
								<IconButton
									Icon={ChevronDown}
									iconStrokeWidth={2}
									variant="ghost"
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										goNext()
									}}
									disabled={!nextThread}
								/>
							</Tooltip>
							<Tooltip
								label={
									<span className="inline-flex gap-2">
										Navigate up <Shortcut>K</Shortcut>
									</span>
								}
							>
								<IconButton
									Icon={ChevronUp}
									iconStrokeWidth={2}
									variant="ghost"
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										goPrev()
									}}
									disabled={!prevThread}
								/>
							</Tooltip>
						</div>
					)}
					{isFocusMode && (
						<IconButton
							Icon={PanelRight}
							iconStrokeWidth={2}
							variant="ghost"
							onClick={() => {
								threadSidebar.open()
							}}
						/>
					)}
				</div>
			</div>
		</div>
	)
}
